import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { loadWordpress } from './store/wordpress/wordpress.actions';
import { loadFixedPages } from './store/fixed-pages/fixedPages.actions';
import { BaseComponentDirective, CssVarsUtils } from '@streamamg/amg-common';
import { NavigationEnd, Router } from '@angular/router';
import { delay, filter, first } from 'rxjs/operators';
import { LoaderService } from '@streamamg/components-lib';
import { CloudPayService } from '@streamamg/cloud-pay-lib';
import { AppState } from './store';
import { selectApplicationReady } from './store/app.selectors';
import { combineLatest } from 'rxjs';
import { WpCommonData } from './types/wordpress/common-data.type';
import { selectCommonData } from './store/wordpress/wordpress.selectors';
import { loadMatches } from './store/fixtures/fixtures.actions';
import { loadTeams } from './store/teams/teams.actions';
import { selectTeams, selectTeamsMap, selectTeamsMatchesMap } from './store/teams/teams.selectors';
import { environment } from '../environments/environment';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import packageJson from '../../package.json';
import { DOCUMENT } from "@angular/common";

// const REFRESH_DATA_MINUTES = 10;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class AppComponent extends BaseComponentDirective implements OnInit {
  public appReady: boolean;
  public loading: boolean;
  public wpCommonData: WpCommonData;
  public showContent: boolean;
  private appVersion = packageJson.version;

  constructor(
    private cloudPayService: CloudPayService, // to trigger and load the script, do not remove
    private store: Store<AppState>,
    private router: Router,
    private cd: ChangeDetectorRef,
    private loaderService: LoaderService,
    private gtmService: GoogleTagManagerService,
    @Inject(DOCUMENT) private document: Document,
  ) {

    super();
    this.appReady = false;
    this.loading = false;
    this.cloudPayService.loadCloudPayScript();
    CssVarsUtils.initScreenSizesOnRoot();
  }

  private static setTeamStyleColor(teamSlug: string, color: string, key: string): string {
    return `
                 [data-team='${teamSlug}'].team-${key}-txt,
                 [data-team='${teamSlug}'] .team-${key}-txt {
                   color: ${color} !important;
                 }

                 [data-team='${teamSlug}'].team-${key}-bg,
                 [data-team='${teamSlug}'] .team-${key}-bg,
                 [data-team='${teamSlug}'] .team-${key}-bg-before:before,
                 [data-team='${teamSlug}'] .team-${key}-bg-after:after,
                 [data-team='${teamSlug}'].team-${key}-bg-before:before,
                 [data-team='${teamSlug}'].team-${key}-bg-after:after {
                   background-color: ${color} !important;
                 }

                 [data-team='${teamSlug}'].team-${key}-border,
                 [data-team='${teamSlug}'] .team-${key}-border {
                    border-color: ${color} !important;
                  }
              `;
  }

  private static setTeamStyleLogo(teamSlug: string, logo: string, key: string): string {
    return `
                 [data-team='${teamSlug}'].team-${key},
                 [data-team='${teamSlug}'] .team-${key},
                 [data-team='${teamSlug}'] .team-${key}-before:before,
                 [data-team='${teamSlug}'] .team-${key}-after:after,
                 [data-team='${teamSlug}'].team-${key}-before:before,
                 [data-team='${teamSlug}'].team-${key}-after:after {
                    background-image: url('${logo}')
                 }
              `;
  }

  ngOnInit(): void {
    this.document.body.parentElement.setAttribute('data-version', this.appVersion);

    this.listenToLoading();
    this.onRouteChange();
    this.storeWpData();

    this.subSink = combineLatest([
      this.store.select(selectApplicationReady),
      this.cloudPayService.cloudPayState(),
    ])
      .subscribe(([
                    dataStoreReady,
                    cloudPayState,
                  ]) => {

        if (dataStoreReady && !cloudPayState.isScriptLoading) {
          this.setTeamStyle();
          this.appReady = true;
          this.cd.markForCheck();

          setTimeout(() => {
            this.showContent = true;
            this.cd.markForCheck();
          }, 1000);
        }
      });

    super.subSink = this.store.select(selectCommonData)
      .subscribe((commonData) => {

        if (commonData) {
          this.wpCommonData = commonData;
          this.cd.markForCheck();
        }
      });

    this.storeSpData();

  }

  /**
   * Listen to the loadingSub property in the LoadingService class. This drives the
   * display of the loading spinner.
   */
  private listenToLoading(): void {
    this.loaderService.loadingSub
      .pipe(delay(0)) // This prevents a ExpressionChangedAfterItHasBeenCheckedError for subsequent requests
      .subscribe((loading) => {
        this.loading = loading;
        this.cd.markForCheck();
      });
  }


  private storeWpData(): void {
    this.store.dispatch(loadTeams());
    this.store.dispatch(loadWordpress());
    this.store.dispatch(loadFixedPages());

  }

  private storeSpData(): void {
    this.subSink = combineLatest([
      this.store.select(selectCommonData),
      this.store.select(selectTeamsMap),
      this.store.select(selectTeamsMatchesMap),
    ])
      .subscribe(([
                    commonData,
                    teamsMap,
                    teamsMatchesMap,
                  ]) => {
        if (commonData && teamsMap && teamsMatchesMap) {
          this.store.dispatch(loadMatches());
        }
      });
  }


  private onRouteChange(): void {

    this.subSink = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd)) // event instanceof NavigationStart ||
      .subscribe((event: NavigationEnd) => { // || NavigationStart

        if (environment.production) {
          const gtmTag = {
            event: 'page',
            pageName: event.url,
          };
          this.gtmService.pushTag(gtmTag);
        }
      });
  }

  private setTeamStyle() {

    super.subSink = this.store.select(selectTeams).pipe(first())
      .subscribe((teams) => {
        if (teams?.length > 0) {

          const styles: string[] = [];

          teams.forEach((team) => {

            const wpTeam = team.wpTeam;
            const teamSlug = wpTeam.post_slug;

            if (wpTeam.color) {
              styles.push(AppComponent.setTeamStyleColor(teamSlug, wpTeam.color, 'color'));
            }
            if (wpTeam.color_secondary) {
              styles.push(AppComponent.setTeamStyleColor(teamSlug, wpTeam.color_secondary, 'color2'));
            }
            if (wpTeam.color_tertiary) {
              styles.push(AppComponent.setTeamStyleColor(teamSlug, wpTeam.color_tertiary, 'color3'));
            }

            if (wpTeam.logo_light_bg) {
              styles.push(AppComponent.setTeamStyleLogo(teamSlug, wpTeam.logo_light_bg.sizes.thumbnail.url, 'logo-bg-light'));
            }
            if (wpTeam.logo_dark_bg) {
              styles.push(AppComponent.setTeamStyleLogo(teamSlug, wpTeam.logo_dark_bg.sizes.thumbnail.url, 'logo-bg-dark'));
            }
            if (wpTeam.logo_team_bg) {
              styles.push(AppComponent.setTeamStyleLogo(teamSlug, wpTeam.logo_team_bg.sizes.thumbnail.url, 'logo-bg-team'));
            }
          });

          const style = document.createElement('style');
          style.innerHTML = styles.join('');
          document.getElementsByTagName('head')[0].appendChild(style);
        }
      });
  }
}
